import { useState } from "react";
import { z } from "zod";
import { axiosFunction } from "../components/commonFunctions/axiosFunction";
import { v2ReactURL } from "../utils/constant";
import { dataTableMetaDTOSchema, Pagination } from "../pages/Contact/MainDataTable";

// Zod schemas for API responses
const DriveDataSchema = z.object({
  fileName: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.string(),
  remark: z.string(),
  documentSize: z.number().nullable(),
  docName: z.string(),
  companyDriveId: z.number(),
});


const DriveApiResponseSchema = z.object({
  status: z.boolean(),
  message: z.string(),
  response: z.object({
    recordsFiltered: z.number(),
    recordsTotal: z.number(),
    draw: z.number(),
    error: z.string().nullable(),
    dataTableMetaDTO: dataTableMetaDTOSchema,
    data: z.array(DriveDataSchema),
  }),
});

// TypeScript interfaces
interface Payload {
  searchValue: string;
  length: number;
  start: number;
  draw: number;
}
interface DriveData {
  fileName: string | null;
  createdAt: string;
  createdBy: string;
  remark: string;
  documentSize: number | null;
  docName: string;
  companyDriveId: number;
}

const useCompanyDriveApi = () => {
  const [driveData, setDriveData] = useState<DriveData[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState<Pagination>({
    page: 0,
    pages: 0,
    perpage: 0,
    total: 0,
  });

  // Fetch data for the company-drive table
  const fetchDataTableApi = async (payload: Payload) => {
    setLoadingData(true);
    try {
      const res = await axiosFunction(
        `${v2ReactURL}/drive/datatable`,
        "POST",
        payload
      );
      const parsed = DriveApiResponseSchema.safeParse(res?.data);
      if (parsed.success) {
        const { data, dataTableMetaDTO } = parsed.data.response;
        setDriveData(data.map((col, index) => ({ ...col, index: index + 1 })));
        setPaginationInfo(dataTableMetaDTO);
      } else {
        console.error("Validation failed:", parsed.error);
      }
    } catch (error) {
      console.error("Unexpected error in fetchDataTableApi", error);
    } finally {
      setLoadingData(false);
    }
  };

  // Add data to the company-drive table
  const createDocument = async (payload: FormData) => {
    try {
      const res = await axiosFunction(
        `${v2ReactURL}/drive/save`,
        "POST",
        payload,
        true
      );
      return res;
    } catch (error) {
      console.error("Unexpected error in createDocument", error);
    }
  };

  // Delete data from the company-drive table
  const deleteDriveRow = async (payload: number[], pageLength: number, startIndex: number) => {
    setLoadingData(true);
    try {
      const res = await axiosFunction(
        `${v2ReactURL}/drive/delete`,
        "DELETE",
        payload,
        false
      );

      if (!res?.error) {
        await fetchDataTableApi({
          searchValue: "",
          length: pageLength,
          start: startIndex,
          draw: 1,
        });
        return res;
      } else {
        console.error("Error in deleteDriveRow:", res.error);
      }
    } catch (error) {
      console.error("Unexpected error in deleteDriveRow", error);
    } finally {
      setLoadingData(false);
    }
  };

  // Download a file from the company-drive table
  const fileDownload = async (payload: { id: number }, fileName: string) => {
    const res = await axiosFunction(
      `${v2ReactURL}/drive/download/${payload.id}`,
      "GET",
      "",
      false,
      "blob"
    );

    const fileType = fileName
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileType);
    document.body.appendChild(link);
    link.click();
  };

  return {
    fetchDataTableApi,
    paginationInfo,
    driveData,
    loadingData,
    setPaginationInfo,
    createDocument,
    fileDownload,
    deleteDriveRow,
  };
};

export default useCompanyDriveApi;
